// ============================================================
// Chess Arena — Game Config Loader (Singleton)
// ============================================================
//
// Loads all JSON configs from the Phaser cache after PreloaderScene
// completes, and provides typed getters for each config section.
// ============================================================

import {
  GameSettingsConfig,
  TrophyBracket,
  ArenaConfig,
  XPLevelConfig,
  TaskConfig,
  LevelConfig,
  AssetEntry,
  ToolConfigEntry,
  AbilityConfigEntry,
} from './types';

// --- Config shape interfaces (not already in types.ts) ---

export interface ProgressionConfig {
  trophyBrackets: TrophyBracket[];
  arenas: ArenaConfig[];
  xpLevels: XPLevelConfig[];
  winStreakBonusPerWin: number;
  winStreakBonusCap: number;
  winStreakMinWins: number;
  coinsPerWin: number;
  xpPerWin: number;
  xpPerLoss: number;
  livesMax: number;
  livesRegenMinutes: number;
  onboardingCompleteLevel: number;
}

export interface SpawnDefaultsConfig {
  player: { timerSeconds: number; weights: Record<string, number> };
  enemy: { timerSeconds: number; weights: Record<string, number> };
}

export interface UITextConfig {
  [key: string]: string | Record<string, string>;
}

export interface TeamColorsConfig {
  player: { primary: string; secondary: string };
  enemy: { primary: string; secondary: string };
}

export interface ProceduralConfig {
  baseDifficulty: number;
  spawnTimerRange: [number, number];
  waveCountRange: [number, number];
  terrainRocksRange: [number, number];
  terrainBushesRange: [number, number];
  hiddenKingAfterLevel: number;
  matchTimerSec: number;
  ratingBase: number;
  ratingPerLevel: number;
  [key: string]: unknown;
}

export interface OpponentPoolEntry {
  name: string;
  portrait: string;
  rating?: number;
}

export interface DailyTasksConfig {
  version: number;
  unlockAfterLevel: number;
  resetTimeUTC: string;
  tasksPerDay: { easy: number; medium: number; hard: number };
  dailyChestReward: { coins: number; xp: number };
  taskPool: TaskConfig[];
}

export interface ChestTierRewards {
  coins: [number, number];
  charges: [number, number];
  shardCount: [number, number];
  shardRarityWeights: Record<string, number>;
}

export interface ChestConfigData {
  tiers: Record<string, {
    unlockTimeMinutes: number;
    dropWeight: number;
    rewards: ChestTierRewards;
  }>;
}

export interface ToolConfigData {
  tools: Record<string, ToolConfigEntry>;
}

export interface AbilityConfigData {
  abilities: Record<string, AbilityConfigEntry>;
  slots: {
    initialSlots: number;
    slotUnlocks: Array<{ slot: number; arenaRequired: number }>;
  };
  rarityColors: Record<string, string>;
}

export interface TutorialConfigData {
  levels: Record<string, {
    steps: Array<{
      stepId: string;
      spotlightTarget: string;
      text: string;
      requiredAction: string | null;
      enemyState: 'frozen' | 'active';
      highlightElement: string;
      aiDialogue: string | null;
    }>;
    aiDialogue?: Array<{ trigger: string; text: string }>;
  }>;
  postOnboarding?: {
    mainMenuSteps: Array<{
      stepId: string;
      spotlightTarget: string | null;
      text: string;
      requiredAction: string | null;
    }>;
  };
}

// --- Config cache keys (must match PreloaderScene load keys) ---

const CONFIG_KEYS = {
  gameSettings: 'gameSettings',
  progression: 'progression',
  spawnDefaults: 'spawnDefaults',
  uiText: 'uiText',
  teamColors: 'teamColors',
  proceduralConfig: 'proceduralConfig',
  opponentPool: 'opponentPool',
  dailyTasks: 'dailyTasks',
  chestConfig: 'chestConfig',
  tutorialConfig: 'tutorialConfig',
  assetManifest: 'assetManifest',
  toolConfig: 'ToolConfig',
  abilityConfig: 'AbilityConfig',
} as const;

class GameConfig {
  private _gameSettings: GameSettingsConfig | null = null;
  private _progression: ProgressionConfig | null = null;
  private _spawnDefaults: SpawnDefaultsConfig | null = null;
  private _uiText: UITextConfig | null = null;
  private _teamColors: TeamColorsConfig | null = null;
  private _proceduralConfig: ProceduralConfig | null = null;
  private _opponentPool: OpponentPoolEntry[] | null = null;
  private _dailyTasks: DailyTasksConfig | null = null;
  private _chestConfig: ChestConfigData | null = null;
  private _tutorialConfig: TutorialConfigData | null = null;
  private _assetManifest: AssetEntry[] | null = null;
  private _toolConfig: ToolConfigData | null = null;
  private _abilityConfig: AbilityConfigData | null = null;
  private _levelConfigs: Map<number, LevelConfig> = new Map();
  private _initialized = false;

  /**
   * Initialize config by reading all loaded JSON from the Phaser cache.
   * Call once after PreloaderScene has finished loading all JSON assets.
   */
  init(cache: Phaser.Cache.CacheManager): void {
    if (this._initialized) {
      console.warn('[GameConfig] Already initialized.');
      return;
    }

    const json = cache.json;

    this._gameSettings = json.get(CONFIG_KEYS.gameSettings) ?? null;
    this._progression = json.get(CONFIG_KEYS.progression) ?? null;
    this._spawnDefaults = json.get(CONFIG_KEYS.spawnDefaults) ?? null;
    this._uiText = json.get(CONFIG_KEYS.uiText) ?? null;
    this._teamColors = json.get(CONFIG_KEYS.teamColors) ?? null;
    this._proceduralConfig = json.get(CONFIG_KEYS.proceduralConfig) ?? null;
    this._opponentPool = json.get(CONFIG_KEYS.opponentPool) ?? null;
    this._dailyTasks = json.get(CONFIG_KEYS.dailyTasks) ?? null;
    this._chestConfig = json.get(CONFIG_KEYS.chestConfig) ?? null;
    this._tutorialConfig = json.get(CONFIG_KEYS.tutorialConfig) ?? null;
    this._assetManifest = json.get(CONFIG_KEYS.assetManifest) ?? null;
    this._toolConfig = json.get(CONFIG_KEYS.toolConfig) ?? null;
    this._abilityConfig = json.get(CONFIG_KEYS.abilityConfig) ?? null;

    // Load hand-tuned level configs (Level01–Level12)
    for (let i = 1; i <= 12; i++) {
      const key = `Level${String(i).padStart(2, '0')}`;
      const levelData = json.get(key) as LevelConfig | undefined;
      if (levelData) {
        this._levelConfigs.set(i, levelData);
      }
    }

    this._initialized = true;
    console.log('[GameConfig] Initialized with configs from Phaser cache.');
  }

  get isInitialized(): boolean {
    return this._initialized;
  }

  getGameSettings(): GameSettingsConfig {
    return this._gameSettings!;
  }

  getProgression(): ProgressionConfig {
    return this._progression!;
  }

  getSpawnDefaults(): SpawnDefaultsConfig {
    return this._spawnDefaults!;
  }

  getUIText(): UITextConfig {
    return this._uiText!;
  }

  getTeamColors(): TeamColorsConfig {
    return this._teamColors!;
  }

  getProceduralConfig(): ProceduralConfig {
    return this._proceduralConfig!;
  }

  getOpponentPool(): OpponentPoolEntry[] {
    return this._opponentPool!;
  }

  getDailyTasks(): DailyTasksConfig {
    return this._dailyTasks!;
  }

  getChestConfig(): ChestConfigData {
    return this._chestConfig!;
  }

  getTutorialConfig(): TutorialConfigData {
    return this._tutorialConfig!;
  }

  getAssetManifest(): AssetEntry[] {
    return this._assetManifest!;
  }

  getToolConfig(): ToolConfigData {
    return this._toolConfig!;
  }

  getAbilityConfig(): AbilityConfigData {
    return this._abilityConfig!;
  }

  /**
   * Get a hand-tuned level config by level number.
   * Returns undefined for levels 13+ (use LevelGenerator instead).
   */
  getLevelConfig(level: number): LevelConfig | undefined {
    return this._levelConfigs.get(level);
  }
}

const gameConfig = new GameConfig();
export default gameConfig;
